/*
 * OzMobiles: colour list for smart-watches products.
 *
 * - Colours in stock for the selected size and condition come first, in their original order, and
 *   are never folded away.
 * - When the list runs past two lines, the sold-out colours after the second line are folded behind
 *   a "+N more" link that takes the end of the second line; it opens the full list and becomes
 *   "Show less". Changing the size (first option) folds it again.
 * - The selected colour is always shown: if it would be folded (a ?variant= link, or a gallery photo
 *   clicked), it takes the last visible place before "+N more", and folds again once deselected.
 *
 * Order changes use the CSS order property only. theme.js addresses swatches by :nth-child, so the
 * DOM order must never change. Stock and existence come from the classes theme.js already sets:
 * color-swatch--disabled (no stock for the selected size and condition) and oz-swatch--absent (the
 * combination does not exist). Phones and every other template are left untouched.
 */
(function () {
  'use strict';

  // Keep in step with theme.js (_onOptionChanged) and custom.js (syncVariantFromThumbnail).
  var TEMPLATES = ['smart-watches'];
  var LINES = 2;

  function productData(section) {
    var el = section.querySelector('[data-product-json]');
    try { return el ? JSON.parse(el.textContent || el.innerHTML) : null; } catch (e) { return null; }
  }

  // The options the customer has chosen, as one string, to tell whether they still match what the
  // server rendered the data-oz-* attributes for.
  // The colour's own position is left out: whether a colour exists or is in stock depends on the
  // other options only, so picking a colour does not make the server's attributes stale.
  function selectionKey(section, colourPosition) {
    return Array.prototype.filter.call(section.querySelectorAll('.product-form__single-selector:checked'), function (input) {
      return input.getAttribute('data-option-position') !== colourPosition;
    }).map(function (input) {
      return input.getAttribute('data-option-position') + '=' + input.value;
    }).sort().join('|');
  }

  // Liquid lists at most 250 variants; oz-high-variants.js sets oz_variants_complete once it has the rest.
  function variantListComplete(data) {
    if (!data || !data.product || !data.product.variants) { return true; }
    var combinations = (data.options_with_values || []).reduce(function (n, option) {
      return n * ((option.values && option.values.length) || 1);
    }, 1);
    return data.product.variants.length < 250 || combinations <= 250 || data.product.oz_variants_complete === true;
  }

  function selectedFirstOption(section) {
    var input = section.querySelector('.product-form__single-selector[data-option-position="1"]:checked');
    return input ? input.value : null;
  }

  function ColourList(section, list) {
    this.section = section;
    this.list = list;
    this.expanded = false;
    this.firstOption = selectedFirstOption(section);
    // The selection the server rendered the data-oz-* attributes for.
    var colourInput = list.querySelector('.color-swatch__radio');
    this.colourPosition = colourInput ? colourInput.getAttribute('data-option-position') : null;
    this.renderedSelection = list.getAttribute('data-oz-rendered-selection') || selectionKey(section, this.colourPosition);
    list.setAttribute('data-oz-rendered-selection', this.renderedSelection);
    list.classList.add('oz-colour-list');

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'oz-colour-more';
    this.button.addEventListener('click', this.toggle.bind(this));
    list.appendChild(this.button);

    // Re-fold whenever the list's own width changes (window resize, the column reflowing, fonts
    // arriving). Folding changes only the height, so this cannot loop.
    var self = this;
    this.width = list.clientWidth;
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(function () {
        if (list.clientWidth !== self.width) {
          self.width = list.clientWidth;
          self.arrange();
        }
      });
      this.observer.observe(list);
    }
  }

  ColourList.prototype.toggle = function () {
    this.expanded = !this.expanded;
    this.arrange();
  };

  // Swatches per line, from the real layout: one swatch's full slot against the list width.
  ColourList.prototype.perLine = function (sample) {
    var style = window.getComputedStyle(sample);
    var slot = sample.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    var listStyle = window.getComputedStyle(this.list);
    var width = this.list.clientWidth - parseFloat(listStyle.paddingLeft) - parseFloat(listStyle.paddingRight);
    return { count: Math.max(1, Math.floor(width / slot)), slot: slot };
  };

  ColourList.prototype.arrange = function () {
    var firstOption = selectedFirstOption(this.section);
    if (firstOption !== this.firstOption) {
      this.firstOption = firstOption;
      this.expanded = false;
    }

    var swatches = Array.prototype.slice.call(this.list.querySelectorAll('.color-swatch'));

    // Until the full variant list is in (a product past 250 variants, first second of the page), the
    // classes theme.js set come from the first 250 only. Use what Shopify rendered for the selected
    // options instead (data-oz-exists / data-oz-available), so the count and order do not jump.
    var serverState = !variantListComplete(productData(this.section)) && selectionKey(this.section, this.colourPosition) === this.renderedSelection;
    var exists = function (s) {
      return serverState ? s.getAttribute('data-oz-exists') !== 'false' : !s.classList.contains('oz-swatch--absent');
    };
    var available = function (s) {
      return serverState ? s.getAttribute('data-oz-available') === 'true' : !s.classList.contains('color-swatch--disabled');
    };
    if (serverState) {
      swatches.forEach(function (s) {
        s.classList.toggle('oz-swatch--absent', !exists(s));
        s.classList.toggle('color-swatch--disabled', !available(s));
      });
    }

    var existing = swatches.filter(exists);
    var inStock = existing.filter(available);
    var soldOut = existing.filter(function (s) { return !available(s); });
    var sequence = inStock.concat(soldOut);
    var selected = existing.filter(function (s) {
      var input = s.querySelector('input');
      return input && input.checked;
    })[0];

    swatches.forEach(function (s) { s.classList.remove('oz-swatch--collapsed'); s.style.order = ''; });

    if (!sequence.length) { this.button.hidden = true; return; }

    var layout = this.perLine(sequence[0]);
    var capacity = layout.count * LINES;

    if (sequence.length <= capacity) {
      sequence.forEach(function (s, i) { s.style.order = i; });
      this.button.hidden = true;
      return;
    }

    if (this.expanded) {
      sequence.forEach(function (s, i) { s.style.order = i; });
      this.button.hidden = false;
      this.button.textContent = 'Show less';
      this.button.style.order = sequence.length;
      return;
    }

    // Measure the link with the longest label it can carry, then give it whole swatch slots.
    this.button.hidden = false;
    this.button.textContent = '+' + sequence.length + ' more';
    var buttonStyle = window.getComputedStyle(this.button);
    var buttonWidth = this.button.offsetWidth + parseFloat(buttonStyle.marginLeft) + parseFloat(buttonStyle.marginRight);
    var buttonSlots = Math.ceil(buttonWidth / layout.slot);

    var shown = Math.max(inStock.length, capacity - buttonSlots);
    var visible = sequence.slice(0, shown);
    if (selected && visible.indexOf(selected) === -1) {
      // The selected colour takes the last visible place, unless that would fold an in-stock one.
      if (shown > inStock.length) { visible = visible.slice(0, shown - 1); }
      visible.push(selected);
    }

    var order = 0;
    visible.forEach(function (s) { s.style.order = order++; });
    this.button.style.order = order++;
    var folded = sequence.filter(function (s) { return visible.indexOf(s) === -1; });
    folded.forEach(function (s) {
      s.style.order = order++;
      s.classList.add('oz-swatch--collapsed');
    });

    this.button.textContent = '+' + folded.length + ' more';
  };

  var lists = [];

  function setUp() {
    lists.forEach(function (colourList) { if (colourList.observer) { colourList.observer.disconnect(); } });
    lists = [];
    document.querySelectorAll('[data-section-type="product"]').forEach(function (section) {
      var data = productData(section);
      if (!data || TEMPLATES.indexOf(data.template) === -1) { return; }
      section.querySelectorAll('.product-form__option[data-selector-type="color"] .color-swatch-list').forEach(function (list) {
        var old = list.querySelector('.oz-colour-more');
        if (old) { old.remove(); }
        var colourList = new ColourList(section, list);
        colourList.arrange();
        lists.push(colourList);
      });
    });
  }

  function arrangeAll() {
    lists.forEach(function (colourList) { colourList.arrange(); });
  }

  function start() {
    // After theme.js has built the sections and set the stock classes on DOMContentLoaded.
    setTimeout(setUp, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // theme.js sets the stock classes in _updateSelectors before it fires variant:changed.
  document.addEventListener('variant:changed', function () { setTimeout(arrangeAll, 0); });
  // oz-high-variants.js re-initialises the section with the full variant list.
  document.addEventListener('oz:product-json-updated', function () { setTimeout(arrangeAll, 0); });

  // Browsers without ResizeObserver fall back to the window resize event.
  if (!window.ResizeObserver) {
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(arrangeAll, 150);
    });
  }
})();
